import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { tmApi, type TmTaskView } from "../../api/tm";
import { TmStatusBadge } from "./TmStatusBadge";
import { TmPriorityPill } from "./TmPriorityPill";

/**
 * Lists direct subtasks of a parent task. Click-through to the child task
 * detail. "Add subtask" routes to the new-task page with `?parentTaskId=...`
 * pre-selected — the new-task form picks it up via the URL.
 */
export function TmSubtasksList({ parentTaskId }: { parentTaskId: string }) {
  const { canWrite } = useSectionAccess("tm_tasks");
  const { t } = useTranslation();
  const [subtasks, setSubtasks] = useState<TmTaskView[] | null>(null);

  useEffect(() => {
    void tmApi.subtasks(parentTaskId).then(setSubtasks).catch(() => setSubtasks([]));
  }, [parentTaskId]);

  if (subtasks === null) {
    return <div style={{ color: "var(--ink-3)", fontSize: 13 }}>{t("common.loading", "Loading…")}</div>;
  }

  const done = subtasks.filter((s) => s.status === "DONE").length;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
          {subtasks.length === 0
            ? t("tm.subtasks.empty", "No subtasks yet.")
            : t("tm.subtasks.progress", { defaultValue: "{{done}} of {{total}} done", done, total: subtasks.length })}
        </span>
        {canWrite && <Link
          to={`/tm/tasks/new?parentTaskId=${parentTaskId}`}
          className="btn sm"
          style={{ textDecoration: "none" }}
        >
          + {t("tm.subtasks.add", "Add subtask")}
        </Link>}
      </div>
      {subtasks.length > 0 && (
        <ul style={listStyle}>
          {subtasks.map((task) => (
            <li key={task.id} style={rowStyle}>
              <Link to={`/tm/tasks/${task.id}`} style={{ flex: 1, color: "var(--ink)", textDecoration: "none", minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {task.title}
                </div>
                <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 2 }}>
                  {task.assigneeName}
                  {task.dueDate ? ` · ${t("tm.tasks.detail.due", "Due")} ${task.dueDate}` : ""}
                </div>
              </Link>
              <TmPriorityPill priority={task.priority} />
              <TmStatusBadge status={task.status} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const listStyle: React.CSSProperties = {
  listStyle: "none", padding: 0, margin: 0,
  display: "flex", flexDirection: "column", gap: 6,
};
const rowStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 10,
  padding: "8px 10px",
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: 8,
};
